import { useTranslation } from 'react-i18next';

import useAiChatToolStatus from '../../hooks/useAiChatToolStatus';
import AiChatLineChartDisplay from '../AiChatLineChartDisplay';
import AiChatPieChartDisplay from '../AiChatPieChartDisplay';
import AiChatDisplayFailed from './AiChatDisplayFailed';
import AiChatDisplayPlaceholder from './AiChatDisplayPlaceholder';

import type { ReactElement } from 'react';
import type { IAiChatToolCallOf } from '../../interfaces/IAiChatToolCall';
import type { TAiChatChartType } from '../../interfaces/IAiChatTools';

/** What the reader calls each kind of chart, for the line that says one could not be shown. */
const CHART_TYPE_KEYS: Record<TAiChatChartType, string> = {
  line: 'aiChat.tools.displayLineChart',
  pie: 'aiChat.tools.displayPieChart',
};

/**
 * The `showChart` tool call: a stored result placed in the reply as a chart.
 *
 * A display tool, inline in the reply like the datasource table. While it runs, the title the
 * model asked for over an empty slot; once it is done, the chart `output.type` names, its rows
 * read whole from the datasource route. A result of `createDatasource` is read the same way: the
 * rows live on the server, never in the part. A call that showed nothing leaves one line saying
 * so, named by the kind of chart the model asked for.
 */
function ShowChartToolCall({ part }: IAiChatToolCallOf<'showChart'>): ReactElement | null {
  const { t } = useTranslation();
  const status = useAiChatToolStatus(part.state);

  if (part.state !== 'output-available') {
    return (
      <AiChatDisplayPlaceholder
        title={part.input?.title || t('aiChat.tools.showChart')}
        status={status}
        errorText={part.errorText}
      />
    );
  }

  const { input, output } = part;

  if (!output.shown) {
    return <AiChatDisplayFailed type={t(CHART_TYPE_KEYS[input.type] ?? 'aiChat.tools.displayChart')} />;
  }

  switch (output.type) {
    case 'line':
      return <AiChatLineChartDisplay shown={output} />;
    case 'pie':
      return <AiChatPieChartDisplay shown={output} />;
    default:
      return null;
  }
}

export default ShowChartToolCall;
